import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_MY_CARDS_CARDS_TYPE } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';

import { RarityVaultHowItWorksDialogWithFetch } from '../RarityVaultHowItWorksDialog/index.withFetch';
import { SealingGenericAvatar } from '../SealingGenericAvatar';
import bg from './assets/bg.webp';

const SEALED_CARD_ASPECT_RATIO = 0.62;

const Root = styled(Vertical)`
  padding: var(--unit);
  background: url(${bg}) no-repeat center center;
  background-size: 100% 100%;
  width: 100%;
  aspect-ratio: ${SEALED_CARD_ASPECT_RATIO};
`;

export const SealingEntryPoint = ({
  total,
  rarity,
}: {
  total?: number;
  rarity?: Rarity;
}) => {
  const { generateSportContextPath } = useSportContext();
  const [showHowItWorksDialog, setShowHowItWorksDialog] = useState(false);
  const cardsTypePath = generateSportContextPath(
    ANY_SPORT_MY_CARDS_CARDS_TYPE,
    {
      params: {
        type: rarity || Rarity.limited,
      },
    }
  );
  return (
    <Root
      center
      style={
        {
          '--avatar-size': 'calc(6 * var(--unit))',
        } as CSSProperties
      }
      gap={2}
    >
      <Vertical center gap={1.5}>
        <SealingGenericAvatar />
        <LabelL bold>
          <FormattedMessage
            id="sealing.entryPoint.title"
            defaultMessage="{count} sealed"
            // Undefined during loading state
            values={{ count: typeof total === 'number' ? total : '-' }}
          />
        </LabelL>
      </Vertical>
      <Vertical>
        <Button
          color="primary"
          size="compact"
          to={`${cardsTypePath}?selectionMode=sealing`}
        >
          <FormattedMessage {...fantasy.startSealing} />
        </Button>
        <RarityVaultHowItWorksDialogWithFetch
          open={showHowItWorksDialog}
          rarity={rarity}
          onClose={() => setShowHowItWorksDialog(false)}
        />
        <Button
          color="transparent"
          size="compact"
          onClick={() => setShowHowItWorksDialog(true)}
        >
          <FormattedMessage {...glossary.learnMore} />
        </Button>
      </Vertical>
    </Root>
  );
};
